import PropTypes from 'prop-types';
import { Navigate } from 'react-router-dom';

import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import PageLoading from '@zenbusiness/zui/PageLoading';

import { RELEASE_SQUARESPACE_WEBSITES_RESELLER } from '#client/constants/experimentsFlags';
import { useDashboardPath } from '#client/hooks/useDashboardPath';
import { useWebsiteEligibility } from '#client/pages/ProductLanding/hooks/useWebsiteEligibility';

const REDIRECT_BY_EXPERIENCE = {
  sqsp: '/products/static-website',
  duda: '/products/static-website-sqsp',
};

/**
 * Routes users between the Duda (`static-website`) and Squarespace (`static-website-sqsp`)
 * product pages based on `useWebsiteEligibility`.
 *
 * - `experience="sqsp"` (default): used on the SQSP page; redirects users who are not
 *   SQSP-eligible (e.g. active Duda owners) to `/products/static-website`.
 * - `experience="duda"`: used on the Duda page; redirects SQSP-eligible users to
 *   `/products/static-website-sqsp` only when that page is reachable (reseller flag on).
 *
 * @param {object} props
 * @param {'sqsp'|'duda'} [props.experience] - Which experience the current page is for; defaults to `sqsp`
 * @param {import('react').ReactNode} props.children
 * @returns {import('react').ReactNode}
 */
function WebsiteEligibilityGate({ experience = 'sqsp', children }) {
  const { isSqspEligible, isDudaEligible, loading } = useWebsiteEligibility();
  const releaseSqspWebsitesReseller = useExperimentFlag(RELEASE_SQUARESPACE_WEBSITES_RESELLER, false);
  const dashboardRedirectPath = useDashboardPath(REDIRECT_BY_EXPERIENCE[experience]);

  if (experience === 'duda' && !releaseSqspWebsitesReseller) {
    return children;
  }

  if (loading) {
    return <PageLoading />;
  }

  const isEligible = experience === 'duda' ? isDudaEligible : isSqspEligible;

  if (!isEligible) {
    return <Navigate to={dashboardRedirectPath} replace />;
  }

  return children;
}

WebsiteEligibilityGate.propTypes = {
  experience: PropTypes.oneOf(['sqsp', 'duda']),
  children: PropTypes.node.isRequired,
};

export default WebsiteEligibilityGate;
